import React, { useState } from 'react';
import { PaymentElement, useElements, useStripe, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { bookingPublishableKey } from './bookingStripeKey';

interface Props {
  clientSecret: string;
  /**
   * All three are carried on return_url so a 3DS redirect can still finish the server-side
   * confirm. `location` is not optional: WebApi is deployed PER VENUE (locationApiBase.ts), and a
   * 3DS return mounts fresh with no memory of which venue was booked. Confirming against the
   * wrong venue hits a DB with no such booking and strands a charged customer.
   */
  bookingId: string;
  intentId: string;
  location: string;
  /**
   * Guest subtotal + guest tax, in cents, as returned by the 202. Never includes the member's $0
   * entitlement seat, and is NEVER computed here — a client-derived total that disagrees with the
   * intent is exactly the bug this flow exists to prevent.
   */
  amountMinor: number;
  buyerName: string;
  buyerEmail: string;
  /**
   * Called ONLY after Stripe reported the intent as genuinely succeeded. The parent's handler is
   * async and makes a network call, so the promise is declared and awaited rather than discarded —
   * a floating rejection here would strand a charged member on a live Pay button.
   */
  onPaid: (intentId: string) => void | Promise<void>;
  /**
   * Told of a card failure. Optional: the message is already shown in this form's own alert, under
   * the Pay button where a phone user is looking, so a parent that echoes it shows it twice.
   */
  onError?: (message: string) => void;
  /**
   * "Change booking": releases the hold so the member can go back to the wizard. Resolves null
   * when the parent has moved on (released, or confirmed an already-paid booking), or with a
   * message to show here when the member has to stay on the card step.
   *
   * Lives inside the form rather than beside it because the two actions race each other over the
   * same hold: releasing while a card is mid-confirm could free seats a charge is about to pay for,
   * and paying while a release is in flight could charge for a hold being cancelled. One busy lock
   * covers both buttons. Omitted, no Back button is shown.
   */
  onBack?: () => Promise<string | null>;
  /**
   * The route a 3DS redirect returns to, carrying guestBookingId / guestIntentId / guestLocation. It
   * must be a page that mounts useGuestHold, which finishes the confirm. Defaults to Book a
   * session (/book).
   */
  returnPath?: string;
}

const stripePublicKey = bookingPublishableKey();
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null;

const formatMoney = (amountMinor: number): string => `$${(amountMinor / 100).toFixed(2)}`;

const PaymentForm: React.FC<Props> = ({
  clientSecret, bookingId, intentId, location, amountMinor, buyerName, buyerEmail, onPaid, onError,
  onBack, returnPath,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [goingBack, setGoingBack] = useState(false);
  const [error, setError] = useState('');

  const goBack = async () => {
    if (!onBack || busy || goingBack) return;
    setError('');
    setGoingBack(true);
    try {
      const message = await onBack();
      if (message) setError(message);
    } catch {
      // The hold's state is unknown, so the card step stays — with something to act on rather
      // than a button that silently did nothing.
      setError('Couldn’t go back just now. Please try again.');
    } finally {
      setGoingBack(false);
    }
  };

  const pay = async () => {
    if (!stripe || !elements || busy || goingBack) return;
    setError('');
    setBusy(true);
    try {
      // Validate and collect the payment method details before confirming (Stripe's required
      // order for the deferred-intent PaymentElement).
      const { error: submitError } = await elements.submit();
      if (submitError) throw new Error(submitError.message ?? 'Please check the card details.');

      // Back to the booking route, NOT /thank-you: that page ignores its query params and
      // renders an affirmative "Enjoy your race!", which on a 3DS redirect would show a success
      // over a booking still AwaitingPayment with a charged card and confirm never called.
      // These params let the landing route finish the server-side confirm itself.
      // guestLocation is load-bearing, not decoration: the per-venue WebApi host is derived from
      // it, and the landing mount has no other way to know which venue holds this booking.
      const returnParams = new URLSearchParams({
        guestBookingId: bookingId, guestIntentId: intentId, guestLocation: location,
      });
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}${returnPath ?? '/book'}?${returnParams}`,
          payment_method_data: {
            billing_details: { name: buyerName, email: buyerEmail },
          },
        },
        redirect: 'if_required',
      });

      if (confirmError) throw new Error(confirmError.message ?? 'Unable to confirm the payment.');
      if (paymentIntent?.status === 'succeeded') {
        // Awaited, not fire-and-forget: the parent confirms server-side here, and a discarded
        // rejection would leave a charged member looking at an enabled Pay button.
        await onPaid(paymentIntent.id);
        return;
      }
      // requires_action: Stripe is mid-3DS/redirect. Leave it alone — do NOT report success and
      // do NOT surface an error, or the member sees a failure on a payment still in flight.
      if (paymentIntent?.status === 'requires_action') return;
      throw new Error('That payment did not complete.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Payment failed. Please try again.';
      setError(message);
      onError?.(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4">
      <label className="form-label text-accent">Payment details (powered by Stripe)</label>
      <div className="card-element-wrapper">
        <PaymentElement
          options={{ layout: 'tabs', wallets: { applePay: 'auto', googlePay: 'auto' } }}
        />
      </div>
      <button
        className="btn btn-primary w-100 mt-4"
        type="button"
        onClick={pay}
        disabled={busy || goingBack}
      >
        {/* "by card", not "for your guests": the card may be paying for the member's own seat. */}
        {busy ? 'Processing payment…' : `Pay ${formatMoney(amountMinor)} by card`}
      </button>
      {onBack && (
        <button
          className="btn btn-outline-light w-100 mt-2"
          type="button"
          onClick={goBack}
          disabled={busy || goingBack}
        >
          {goingBack ? 'Going back…' : 'Change booking'}
        </button>
      )}
      {/* The ONE place a card or go-back error is shown on this step (Stripe's own inline line in
          the card field aside). Below both buttons, so it sits where a phone user is looking. */}
      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

const GuestPaymentForm: React.FC<Props> = (props) => {
  if (!stripePromise) return null;
  return (
    <Elements
      key={props.clientSecret}
      stripe={stripePromise}
      options={{
        clientSecret: props.clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#128200',
            colorBackground: '#ffffff',
            colorText: '#333333',
            colorDanger: '#ed1c24',
            fontFamily: 'Roboto Condensed, sans-serif',
            fontSizeBase: '16px',
            spacingUnit: '4px',
            borderRadius: '8px',
          },
          rules: {
            '.Input': {
              backgroundColor: '#ffffff',
              border: '1px solid #cccccc',
              color: '#333333',
            },
            '.Input:focus': {
              borderColor: '#128200',
              boxShadow: '0 0 0 0.2rem rgba(18, 130, 0, 0.25)',
            },
            '.Input--invalid': { borderColor: '#ed1c24' },
            '.Label': { color: '#000000' },
            '.Tab': {
              backgroundColor: 'transparent',
              border: '1px solid #cccccc',
              color: '#333333',
            },
            '.Tab--selected': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              borderColor: '#000000',
              color: '#000000',
            },
          },
        },
      }}
    >
      <PaymentForm {...props} />
    </Elements>
  );
};

export default GuestPaymentForm;
